// HotelDetailPage — Full hotel detail with gallery, tabs, sticky sidebar.
// All CTAs → notYetWiredToast (hotels BE doesn't exist).
// TODO(hotels-API-PR): Replace mock with /api/v1/hotels/:id

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Share2, Heart, Star, MapPin, ChevronLeft } from 'lucide-react';
import { GalleryHero } from '@/components/GalleryHero';
import { AmenitiesGrid } from '@/components/AmenitiesGrid';
import { RoomCard } from '@/components/RoomCard';
import { notYetWiredToast } from '@/components/primitives';
import { HOTELS, AMENITIES, ROOMS_PER_HOTEL, SAMPLE_REVIEWS, type HotelMock, type RoomOption } from '@/lib/hotels-mock';
import { useIsMobile } from '@/hooks/useIsMobile';

const T = {
  bg: '#FFF5EE', surface: '#FFFFFF', ink: '#1A0F08', inkDim: '#7A6657',
  primary: '#FF5A3C', accent: '#FFB800', soft: '#FFE3D2',
  line: 'rgba(26,15,8,0.08)', lineStr: '#E8E0D8',
  sans: '"Inter Variable","Inter",system-ui,sans-serif',
  display: '"Fraunces Variable","Fraunces",Georgia,serif',
  grotesk: '"Space Grotesk",system-ui,sans-serif',
};

type Tab = 'overview' | 'rooms' | 'reviews' | 'location';

const STATIC_MAP = 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=900&q=75&auto=format&fit=crop';

// Default hotel used as fallback — always defined.
const FALLBACK_HOTEL: HotelMock = {
  id: 'h2', name: 'Potato Head Suites', area: 'Seminyak', distance: 'On the beach',
  img: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80&auto=format&fit=crop',
  imgs: [
    'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&q=80&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&q=80&auto=format&fit=crop',
  ],
  rating: 9.6, ratingLabel: 'Iconic', reviews: 1204,
  price: 298, oldPrice: 340, perks: ['Beachfront', 'Pool', 'Bar', 'Gym'],
  tag: 'EDITORS PICK', stars: 5, freeCancellation: true, lat: -8.6900, lng: 115.1571,
};

export function HotelDetailPage() {
  const { hotelId } = useParams<{ hotelId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // fall back to FALLBACK_HOTEL if id not matched.
  const h: HotelMock = HOTELS.find(x => x.id === hotelId) ?? FALLBACK_HOTEL;
  const defaultRooms: RoomOption[] = ROOMS_PER_HOTEL['default'] ?? [];
  const rooms: RoomOption[] = ROOMS_PER_HOTEL[h.id] ?? defaultRooms;

  const [tab, setTab] = useState<Tab>('overview');
  const [saved, setSaved] = useState(false);

  const TABS: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'rooms', label: 'Rooms' },
    { key: 'reviews', label: 'Reviews' },
    { key: 'location', label: 'Location' },
  ];

  return (
    <div style={{ background: T.bg, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px clamp(16px, 4vw, 32px) 80px' }}>
        {/* breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18 }}>
          <button
            onClick={() => navigate('/hotels')}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}
          >
            <ChevronLeft size={14} stroke={T.inkDim} strokeWidth={1.8}/>
            <span style={{ fontFamily: T.sans, fontSize: 12, color: T.inkDim }}>Hotels</span>
          </button>
          <span style={{ fontFamily: T.sans, fontSize: 12, color: T.inkDim }}>/</span>
          <span style={{ fontFamily: T.sans, fontSize: 12, color: T.inkDim }}>Bali</span>
          <span style={{ fontFamily: T.sans, fontSize: 12, color: T.inkDim }}>/</span>
          <span style={{ fontFamily: T.sans, fontSize: 12, color: T.ink }}>{h.name}</span>
        </div>

        {/* gallery */}
        <GalleryHero imgs={h.imgs} alt={h.name} onViewAll={() => notYetWiredToast('Photo gallery')}/>

        {/* name + meta */}
        <div style={{ marginTop: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontFamily: T.sans, fontSize: 11, color: T.inkDim, letterSpacing: 1.5, fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>
              {h.area} · Bali · {h.distance}
            </div>
            <h1 style={{
              fontFamily: T.grotesk, fontSize: 44, fontWeight: 700, color: T.ink,
              margin: 0, lineHeight: 1, letterSpacing: -0.02,
            }}>{h.name}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 10, background: T.ink, color: '#fff', fontFamily: T.sans, fontSize: 13, fontWeight: 700 }}>
                <Star size={12} fill="#FFB800" stroke="none"/> {h.rating}
              </span>
              <span style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.ink }}>{h.ratingLabel}</span>
              <span style={{ fontFamily: T.sans, fontSize: 12, color: T.inkDim }}>· {h.reviews.toLocaleString('en-IN')} reviews</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontFamily: T.sans, fontSize: 12, color: T.inkDim }}>
                <MapPin size={12} stroke={T.inkDim} strokeWidth={1.8}/> {h.distance}
              </span>
            </div>
          </div>
          {/* share + save */}
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              onClick={() => notYetWiredToast('Share')}
              style={{ width: 40, height: 40, borderRadius: '50%', border: `1px solid ${T.lineStr}`, background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <Share2 size={16} stroke={T.ink} strokeWidth={1.8}/>
            </button>
            <button
              onClick={() => setSaved(s => !s)}
              style={{ width: 40, height: 40, borderRadius: '50%', border: `1px solid ${T.lineStr}`, background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <Heart size={16} stroke={saved ? T.primary : T.ink} fill={saved ? T.primary : 'none'} strokeWidth={1.8}/>
            </button>
          </div>
        </div>

        {/* tabs + content + sidebar */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 280px', gap: 40, marginTop: 32 }}>
          <div>
            {/* sticky tabs */}
            <div style={{
              display: 'flex', gap: 0, borderBottom: `1px solid ${T.lineStr}`,
              position: 'sticky', top: 80, background: T.bg, zIndex: 10,
            }}>
              {TABS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  style={{
                    padding: '12px 0', marginRight: 24, border: 'none', background: 'none',
                    borderBottom: `2px solid ${tab === key ? T.ink : 'transparent'}`,
                    fontFamily: T.sans, fontSize: 13, fontWeight: 600,
                    color: tab === key ? T.ink : T.inkDim, cursor: 'pointer',
                    transition: 'color 150ms ease, border-color 150ms ease',
                  }}
                >{label}</button>
              ))}
            </div>

            {/* tab panels */}
            <div style={{ paddingTop: 28 }}>
              {tab === 'overview' && <OverviewTab h={h}/>}
              {tab === 'rooms' && <RoomsTab rooms={rooms}/>}
              {tab === 'reviews' && <ReviewsTab rating={h.rating} reviews={SAMPLE_REVIEWS}/>}
              {tab === 'location' && <LocationTab name={h.name}/>}
            </div>
          </div>

          {/* sticky reserve sidebar */}
          <div style={{ position: 'sticky', top: 88, alignSelf: 'flex-start' }}>
            <ReserveSidebar hotel={h}/>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab panels ──────────────────────────────────────────────────────────────

function OverviewTab({ h }: { h: HotelMock }) {
  return (
    <div>
      {/* description */}
      <p style={{ fontFamily: T.sans, fontSize: 15, color: T.ink, lineHeight: 1.6, margin: '0 0 16px', maxWidth: 660 }}>
        On {h.area}'s most sought-after stretch, {h.name} is where the chefs, surfers and DJs all crash.
        Every surface is considered — hand-laid terrazzo, vintage record collections, and a sunset terrace
        that's exactly as good as the photos suggest.
      </p>
      <p style={{ fontFamily: T.sans, fontSize: 15, color: T.ink, lineHeight: 1.6, margin: '0 0 16px', maxWidth: 660 }}>
        Rooms range from intimate studios facing the garden to sprawling beachfront villas with plunge pools.
        All come with high-thread-count linens, rainfall showers, and honesty bars stocked with local craft spirits.
      </p>
      <p style={{ fontFamily: T.sans, fontSize: 15, color: T.ink, lineHeight: 1.6, margin: '0 0 32px', maxWidth: 660 }}>
        Breakfast is served until noon — because this is the kind of place where time slows down and
        nobody judges a 10am start. The spa, pool, and beach club are complimentary for all guests.
      </p>

      {/* amenities */}
      <div style={{ fontFamily: T.sans, fontSize: 11, color: T.inkDim, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 14 }}>
        WHAT'S INCLUDED
      </div>
      <AmenitiesGrid amenities={AMENITIES} columns={3}/>

      {/* highlights */}
      <div style={{ marginTop: 32 }}>
        <div style={{ fontFamily: T.sans, fontSize: 11, color: T.inkDim, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 14 }}>
          HIGHLIGHTS
        </div>
        <ul style={{ margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            'Infinity pool with direct ocean sightlines',
            'Curated record bar open until midnight',
            'In-house organic farm supplies the restaurant',
            '24-hour concierge with real local knowledge',
            'Electric scooters for guest use at no charge',
          ].map(item => (
            <li key={item} style={{ fontFamily: T.sans, fontSize: 14, color: T.ink, lineHeight: 1.5 }}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function RoomsTab({ rooms }: { rooms: RoomOption[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {rooms.map((r, i) => (
        <div key={i} style={{ animation: 'fade-up 0.4s ease both', animationDelay: `${i * 60}ms` }}>
          <RoomCard room={r}/>
        </div>
      ))}
    </div>
  );
}

function ReviewsTab({ rating, reviews }: { rating: number; reviews: typeof SAMPLE_REVIEWS }) {
  const bars = [
    { label: 'Cleanliness', score: 9.8 },
    { label: 'Location', score: 9.5 },
    { label: 'Service', score: 9.6 },
    { label: 'Value', score: 8.9 },
  ];

  return (
    <div>
      {/* overall score */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 28 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: T.display, fontSize: 56, fontWeight: 600, color: T.ink, lineHeight: 1 }}>{rating}</div>
          <div style={{ fontFamily: T.sans, fontSize: 12, color: T.inkDim, marginTop: 4 }}>out of 10</div>
        </div>
        <div style={{ flex: 1 }}>
          {bars.map(b => (
            <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontFamily: T.sans, fontSize: 12, color: T.inkDim, width: 90, flexShrink: 0 }}>{b.label}</span>
              <div style={{ flex: 1, height: 6, borderRadius: 3, background: T.lineStr, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${b.score * 10}%`, background: T.ink, borderRadius: 3 }}/>
              </div>
              <span style={{ fontFamily: T.sans, fontSize: 12, fontWeight: 600, color: T.ink, width: 28 }}>{b.score}</span>
            </div>
          ))}
        </div>
      </div>

      {/* review cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {reviews.map((r, i) => (
          <div key={i} style={{ background: T.surface, borderRadius: 20, padding: 20, border: `1px solid ${T.line}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <img src={r.avatar} alt={r.user} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} loading="lazy"/>
              <div>
                <div style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.ink }}>{r.user}</div>
                <div style={{ fontFamily: T.sans, fontSize: 11, color: T.inkDim }}>{r.date}</div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Star size={13} fill={T.accent} stroke="none"/>
                <span style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 700, color: T.ink }}>{r.rating}</span>
              </div>
            </div>
            <p style={{ fontFamily: T.sans, fontSize: 14, color: T.ink, lineHeight: 1.55, margin: 0 }}>{r.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LocationTab({ name }: { name: string }) {
  return (
    <div>
      <div style={{ borderRadius: 22, overflow: 'hidden', position: 'relative', height: 360 }}>
        <img src={STATIC_MAP} alt="Map" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(0.8) hue-rotate(-10deg)' }} loading="lazy"/>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(245,238,225,0.25)' }}/>
        {/* fake pin */}
        <div style={{ position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <div style={{ padding: '6px 12px', borderRadius: 999, background: T.primary, color: '#fff', fontFamily: T.sans, fontSize: 12, fontWeight: 700, boxShadow: '0 4px 12px rgba(255,90,60,0.4)', border: '2px solid #fff' }}>
            {name}
          </div>
        </div>
      </div>
      <div style={{ marginTop: 16, fontFamily: T.sans, fontSize: 13, color: T.inkDim }}>
        <MapPin size={14} stroke={T.inkDim} style={{ display: 'inline', marginRight: 4 }}/>
        Jl. Petitenget No.51B, Seminyak, Kec. Kuta Utara, Kabupaten Badung, Bali 80361, Indonesia
      </div>
    </div>
  );
}

// ── Sticky reserve sidebar ──────────────────────────────────────────────────

function ReserveSidebar({ hotel: h }: { hotel: HotelMock }) {
  return (
    <div style={{ background: T.surface, borderRadius: 26, padding: 24, border: `1px solid ${T.lineStr}`, boxShadow: '0 12px 24px -8px rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <span style={{ fontFamily: T.display, fontSize: 30, fontWeight: 600, color: T.ink, letterSpacing: -0.01 }}>
            ₹{(h.price * 83).toLocaleString('en-IN')}
          </span>
          <span style={{ fontFamily: T.sans, fontSize: 13, color: T.inkDim, marginLeft: 4 }}>/ night</span>
        </div>
        <span style={{ fontFamily: T.sans, fontSize: 11, color: T.primary, fontWeight: 600 }}>● 4 left</span>
      </div>

      {/* date pickers (visual only) */}
      <div style={{ borderRadius: 16, border: `1px solid ${T.lineStr}`, overflow: 'hidden', marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', borderBottom: `1px solid ${T.lineStr}` }}>
          <div style={{ padding: 12, borderRight: `1px solid ${T.lineStr}`, cursor: 'pointer' }} onClick={() => notYetWiredToast('Date picker')}>
            <div style={{ fontFamily: T.sans, fontSize: 9, color: T.inkDim, letterSpacing: 1, fontWeight: 600, textTransform: 'uppercase' }}>CHECK-IN</div>
            <div style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.ink, marginTop: 2 }}>Mar 15, 2026</div>
          </div>
          <div style={{ padding: 12, cursor: 'pointer' }} onClick={() => notYetWiredToast('Date picker')}>
            <div style={{ fontFamily: T.sans, fontSize: 9, color: T.inkDim, letterSpacing: 1, fontWeight: 600, textTransform: 'uppercase' }}>CHECK-OUT</div>
            <div style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.ink, marginTop: 2 }}>Mar 18, 2026</div>
          </div>
        </div>
        <div style={{ padding: 12, cursor: 'pointer' }} onClick={() => notYetWiredToast('Guest picker')}>
          <div style={{ fontFamily: T.sans, fontSize: 9, color: T.inkDim, letterSpacing: 1, fontWeight: 600, textTransform: 'uppercase' }}>GUESTS</div>
          <div style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.ink, marginTop: 2 }}>2 adults · 1 room</div>
        </div>
      </div>

      {/* price breakdown */}
      <div style={{ marginBottom: 12 }}>
        {[
          [`₹${(h.price * 83).toLocaleString('en-IN')} × 3 nights`, `₹${(h.price * 83 * 3).toLocaleString('en-IN')}`],
          ['Taxes & fees', `₹${(h.price * 83 * 3 * 0.12).toFixed(0)}`],
        ].map(([l, v], i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: T.sans, fontSize: 12, color: T.inkDim, marginBottom: 6 }}>
            <span>{l}</span><span>{v}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: T.sans, fontSize: 14, fontWeight: 700, color: T.ink, paddingTop: 10, borderTop: `1px solid ${T.lineStr}` }}>
          <span>Total</span>
          <span>₹{(h.price * 83 * 3 * 1.12).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</span>
        </div>
      </div>

      <button
        onClick={() => notYetWiredToast('Hotels')}
        style={{
          width: '100%', height: 52, borderRadius: 14,
          background: T.primary, color: '#fff', border: 'none',
          fontFamily: T.sans, fontSize: 15, fontWeight: 600, cursor: 'pointer',
          transition: 'background 200ms ease',
          boxShadow: '0 6px 16px rgba(255,90,60,0.20)',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#E84B30'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = T.primary; }}
      >
        reserve
      </button>
      <div style={{ fontFamily: T.sans, fontSize: 11, color: T.inkDim, textAlign: 'center', marginTop: 8 }}>
        you won't be charged yet
      </div>
    </div>
  );
}
